import {
  Expose,
  Type,
  instanceToPlain,
  plainToInstance,
} from '@infrastructure/domain';
import { ReportCollection } from '@domain/Report';
import type { ReportCollectionX1 } from '@domain/Report';
import type * as Types from '@interfaces/xingOne';
import { SlugOrID, ID, Email, URL } from '@domain/scalars';
import { Consultant } from '@domain/Consultant';
import type { XingUserX1 } from '@domain/XingUser';
import { XingUser } from '../XingUser';

export type LanguageX1 = Types.ErecruitingSourcingServiceLanguage;
export type CareerLevelX1 = Types.CareerLevel;
export type CityX1 = Types.City;
export type CompanyX1 = Pick<
  Types.Company,
  'id' | 'companyName' | 'logos' | 'links'
>;
export type Currency = Types.Currency;
export type DisciplineX1 = Types.Discipline;
export type IndustryX1 = Types.Industry;
export type IndustrySegmentX1 = Types.IndustrySegment;
export type SalaryRangeX1 = Types.ErecruitingSourcingServiceSalaryRange;
export type CoordinatesX1 = Types.ErecruitingSourcingServiceBriefingCoordinates;
export type ProductType = Types.ErecruitingSourcingServiceProductTypeLowCase;
export type ProductTypeDeprecated = Types.ErecruitingSourcingServiceProductType;
export type XingId = Types.XingId;
export type BriefingX1 = Subset<Types.ErecruitingSourcingServiceBriefing>;

class CareerLevel implements CareerLevelX1 {
  @Expose()
  id: SlugOrID;

  @Expose()
  localizationValue: Types.Maybe<string>;

  @Expose()
  localizationKey: Types.Maybe<string>;
}

class City implements Pick<CityX1, 'name'> {
  @Expose()
  name: Types.Maybe<string>;
}

class Discipline implements DisciplineX1 {
  @Expose()
  id: SlugOrID;

  @Expose()
  localizationValue: Types.Maybe<string>;

  @Expose()
  localizationKey: Types.Maybe<string>;

  @Expose()
  name: Types.Maybe<string>;
}

class Industry implements IndustryX1 {
  @Expose()
  id: SlugOrID;

  @Expose()
  localizationValue: Types.Maybe<string>;

  @Expose()
  localizationKey: Types.Maybe<string>;

  @Expose()
  segments: IndustrySegmentX1[];
}

class Language implements LanguageX1 {
  @Expose()
  code: Types.Maybe<string>;

  @Expose()
  levelId: Types.Maybe<number>;
}

class CompanyLogos implements Types.CompanyLogos {
  @Expose()
  logo48px: string;
}

class CompanyLinks implements Types.CompanyLinks {
  @Expose()
  public?: string;
}

class Company implements CompanyX1 {
  @Expose()
  id: string | number;

  @Expose()
  companyName?: string;

  @Expose()
  @Type(() => CompanyLinks)
  links?: CompanyLinks;

  @Expose()
  @Type(() => CompanyLogos)
  logos?: CompanyLogos;
}

export class SalaryRange implements SalaryRangeX1 {
  @Expose()
  minimum: Types.Maybe<number>;

  @Expose()
  maximum: Types.Maybe<number>;
}

export class Coordinates implements CoordinatesX1 {
  @Expose()
  longitude: Types.Maybe<number>;

  @Expose()
  latitude: Types.Maybe<number>;
}

export class Briefing implements BriefingX1 {
  static fromData(data: Subset<BriefingX1>): Briefing | null {
    if (!data || !data.id) {
      return null;
    }

    const instance = plainToInstance(Briefing, data);

    if (data?.reports) {
      instance.reports = ReportCollection.fromData(data.reports);
    }

    return instance;
  }

  static fromDataCollection(collection: Subset<BriefingX1>[]): Briefing[] {
    return collection.map((data) => Briefing.fromData(data));
  }

  setConsultant(consultant: XingUserX1): void {
    this.consultant = Consultant.fromData(consultant);
  }

  toData(): Record<string, any> {
    const data = instanceToPlain<Briefing>(this);

    return {
      ...data,
      reports: undefined,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }

  @Expose()
  id: SlugOrID;

  @Expose()
  archived: Types.Maybe<boolean>;

  @Expose()
  businessEmail: Email;

  @Expose()
  businessPhoneNumber: string;

  @Expose()
  careerLevelId: number;

  @Expose()
  @Type(() => CareerLevel)
  careerLevel: Types.Maybe<CareerLevelX1>;

  @Expose()
  get careerLevelName() {
    return this.careerLevel.localizationValue;
  }

  @Expose()
  @Type(() => Company)
  company: Types.Maybe<Company>;

  @Expose()
  companyId: Types.Maybe<ID>;

  @Expose()
  companyName: string;

  @Expose()
  get companyLogo() {
    return this.company?.logos?.logo48px;
  }

  @Expose()
  companyNotes: Types.Maybe<string>;

  @Expose()
  @Type(() => Date)
  createdAt: Types.Maybe<Date>;

  @Expose()
  creatorId: ID;

  @Expose()
  @Type(() => XingUser)
  creator: Types.Maybe<XingUser>;

  @Expose()
  @Type(() => String)
  currencyCode: Types.Maybe<Currency>;

  @Expose()
  disciplineId: number;

  @Expose()
  @Type(() => Discipline)
  discipline: Types.Maybe<DisciplineX1>;

  @Expose()
  get disciplineName() {
    return this.discipline.localizationValue;
  }

  @Expose()
  education: Types.Maybe<string>;

  @Expose()
  courseOfStudy: Types.Maybe<string>;

  @Expose()
  degree: Types.Maybe<string>;

  @Expose()
  industryId: number;

  @Expose()
  @Type(() => Industry)
  industry: Types.Maybe<IndustryX1>;

  @Expose()
  get industryName() {
    return this.industry.localizationValue;
  }

  @Expose()
  jobDescription: string;

  @Expose()
  jobLocationId: number;

  @Expose()
  @Type(() => City)
  jobLocation: Types.Maybe<CityX1>;

  @Expose()
  get jobLocationName() {
    return this.jobLocation.name;
  }

  @Expose()
  jobPostCode: Types.Maybe<string>;

  @Expose()
  jobroleId: Types.Maybe<number>;

  @Expose()
  jobTitle: string;

  @Expose()
  jobType: string;

  @Expose()
  leadershipResponsibility: Types.Maybe<string>;

  @Expose()
  linkedRecordId: Types.Maybe<number>;

  @Expose()
  linkedRecordSource: Types.Maybe<URL>;

  @Expose()
  linkedRecordType: Types.Maybe<string>;

  @Expose()
  previousJobroleId: Types.Maybe<number>;

  @Expose()
  previousPosition: Types.Maybe<string>;

  @Expose()
  referenceProfileId: Types.Maybe<ID>;

  @Expose()
  @Type(() => XingUser)
  referenceProfile: Types.Maybe<XingUser>;

  @Expose()
  @Type(() => Language)
  requiredLanguages: Types.Maybe<LanguageX1[]>;

  @Expose()
  requiredSkills: Types.Maybe<string[]>;

  @Expose()
  @Type(() => SalaryRange)
  salaryRange: Types.Maybe<SalaryRangeX1>;

  @Expose()
  @Type(() => Date)
  updatedAt: Types.Maybe<Date>;

  @Expose()
  workExperienceId: Types.Maybe<number>;

  @Expose()
  xtmProjectId: ID;

  @Expose()
  isShared: boolean;

  @Expose()
  workflowStatus: Types.Maybe<any>;

  @Expose()
  productTypeNew: Types.Maybe<ProductType>;

  @Expose()
  productType: Types.Maybe<ProductTypeDeprecated>;

  @Expose()
  fullRemote: boolean;

  @Expose()
  positionCountryCode: Types.Maybe<string>;

  @Expose()
  positionLocationIds: Types.Maybe<number[]>;

  @Expose()
  @Type(() => Coordinates)
  positionLocationCoordinates: Types.Maybe<CoordinatesX1[]>;

  @Expose()
  positionLocationCities: Types.Maybe<string[]>;

  @Expose()
  positionRadius: Types.Maybe<number>;

  @Expose()
  jobLocationCity: Types.Maybe<string>;

  @Expose()
  @Type(() => ReportCollection)
  reports: ReportCollectionX1;

  @Expose()
  @Type(() => Consultant)
  consultant: Types.Maybe<Consultant>;
}
